import { StatusModal } from '@/components/StatusModal';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useState } from 'react';

type LatkaHit = { id: string; name: string; code: string; type: string; image: string };

type Role = {
  id: number;
  serial_number: number;
  latka_type: string;
  unit: string;
  stock_qty: number;
  folded: number;
  received_at: number | null;
};

type Objednana = {
  id: number;
  serial_number: number;
  unit: string;
  quantity: number;
  supplier: string;
  expected_at: number | null;
};

type Produkt = {
  code: string;
  name: string;
  image: string;
  second_quality: boolean;
  sizes: { label: string; qty: number | null }[];
};

type Detail = {
  latka: LatkaHit & { price: string; supplier: string };
  products: Produkt[];
  on_stock: Role[];
  ordered: Objednana[];
};

/** Metry s jedním desetinným místem. */
function fmtQty(q: number, unit: string): string {
  const n = Math.round(q * 10) / 10;
  return `${String(n).replace('.', ',')} ${unit || 'm'}`;
}

/** Unix ms → „5.8.2026". */
function fmtDate(ms: number | null): string {
  if (!ms) return '—';
  const d = new Date(ms);
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

/**
 * Informace o látce — střihárna si najde látku a vidí pohromadě, co se z ní
 * šije (včetně skladovosti velikostí v e-shopu) a kolik jí je na dílně.
 */
export function InformaceOLatcePage() {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<LatkaHit[]>([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  // Našeptávač — seznam látek je malý (stovky), filtruje se na backendu.
  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      apiFetch<{ latky: LatkaHit[] }>(`/api/vyroba/striharna/latky?q=${encodeURIComponent(query)}`)
        .then((d) => {
          if (!cancelled) setHits(d.latky);
        })
        .catch(() => {
          if (!cancelled) setHits([]);
        });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  const vybrat = useCallback(async (l: LatkaHit) => {
    setOpen(false);
    setQuery(l.name);
    setLoading(true);
    try {
      setDetail(
        await apiFetch<Detail>(`/api/vyroba/striharna/latka?id=${encodeURIComponent(l.id)}`),
      );
    } catch (e: unknown) {
      setDetail(null);
      setToast({
        msg: e instanceof ApiError ? e.describe() : 'Načtení látky selhalo',
        kind: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const metru = (detail?.on_stock ?? []).reduce((s, r) => s + r.stock_qty, 0);
  const objednano = (detail?.ordered ?? []).reduce((s, r) => s + r.quantity, 0);
  const unit = detail?.on_stock[0]?.unit ?? detail?.ordered[0]?.unit ?? 'm';

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Informace o látce</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Najdi látku a uvidíš, co se z ní šije, jak jsou na tom velikosti v e-shopu a kolik jí máme
          na dílně.
        </p>
      </div>

      {/* Vyhledávání */}
      <div className="relative max-w-xl mb-6">
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Hledat látku podle názvu nebo kódu…"
          className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-base text-text focus:outline-none focus:border-primary"
        />
        {open && hits.length > 0 && (
          <ul className="absolute z-20 mt-1 w-full max-h-80 overflow-y-auto rounded-lg border border-border bg-surface shadow-modal">
            {hits.map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  onClick={() => vybrat(l)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-surface-hover cursor-pointer transition-colors"
                >
                  {l.image ? (
                    <img
                      src={l.image}
                      alt=""
                      loading="lazy"
                      className="w-10 h-10 rounded object-cover border border-border bg-bg shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded border border-border bg-bg shrink-0" />
                  )}
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-text truncate">{l.name}</span>
                    <span className="block text-xs text-text-muted truncate">
                      {[l.code, l.type].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {loading && <p className="text-sm text-text-muted">Načítám…</p>}

      {detail && !loading && (
        <>
          {/* Hlavička látky + skladovost */}
          <div className="bg-surface rounded-xl shadow-card p-5 mb-6 flex gap-5 flex-wrap">
            {detail.latka.image ? (
              <img
                src={detail.latka.image}
                alt=""
                className="w-32 h-32 rounded-lg object-cover border border-border bg-bg shrink-0"
              />
            ) : (
              <div className="w-32 h-32 rounded-lg border border-border bg-bg shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-bold text-text">{detail.latka.name}</h2>
              <div className="text-[15px] text-text-secondary mt-0.5">
                {[detail.latka.code, detail.latka.type, detail.latka.supplier]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              <div className="mt-3 flex gap-6 flex-wrap text-[15px]">
                <div>
                  <div className="text-xs text-text-muted uppercase tracking-wider">Na skladě</div>
                  <div className="text-2xl font-bold text-action-dark">{fmtQty(metru, unit)}</div>
                  <div className="text-sm text-text-muted">{detail.on_stock.length} rolí</div>
                </div>
                {objednano > 0 && (
                  <div>
                    <div className="text-xs text-text-muted uppercase tracking-wider">
                      🚚 Objednáno
                    </div>
                    <div className="text-2xl font-bold text-warning">{fmtQty(objednano, unit)}</div>
                    <div className="text-sm text-text-muted">{detail.ordered.length} rolí</div>
                  </div>
                )}
                <div>
                  <div className="text-xs text-text-muted uppercase tracking-wider">Produktů</div>
                  <div className="text-2xl font-bold text-text">{detail.products.length}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Role */}
          {(detail.on_stock.length > 0 || detail.ordered.length > 0) && (
            <div className="bg-surface rounded-xl shadow-card p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-bold text-text mb-2">Role na skladě</h3>
                <ul className="divide-y divide-border-light">
                  {detail.on_stock.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-3 py-1.5">
                      <span className="text-[15px] text-text">
                        <span className="font-semibold">č. {r.serial_number}</span>
                        <span className="text-text-muted"> · {r.folded ? 'Složené' : 'Role'}</span>
                      </span>
                      <span className="text-[15px] font-semibold text-action-dark whitespace-nowrap">
                        {fmtQty(r.stock_qty, r.unit)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-bold text-text mb-2">Objednáno</h3>
                <ul className="divide-y divide-border-light">
                  {detail.ordered.map((o) => (
                    <li key={o.id} className="flex items-center justify-between gap-3 py-1.5">
                      <span className="text-[15px] text-text">
                        🚚 <span className="font-semibold">č. {o.serial_number}</span>
                        {o.supplier && <span className="text-text-muted"> · {o.supplier}</span>}
                      </span>
                      <span className="text-[15px] text-text-secondary whitespace-nowrap">
                        {fmtQty(o.quantity, o.unit)} · {fmtDate(o.expected_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Produkty z látky */}
          <h3 className="text-xl font-bold text-text mb-3">Produkty z této látky</h3>
          {detail.products.length === 0 ? (
            <p className="text-sm text-text-muted">Z téhle látky zatím nic nešijeme.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {detail.products.map((p) => (
                <div
                  key={p.code}
                  className={`rounded-xl p-4 border bg-surface ${
                    p.second_quality ? 'border-warning/40 bg-warning-light' : 'border-border'
                  }`}
                >
                  <div className="flex gap-4">
                    {p.image ? (
                      <img
                        src={p.image}
                        alt=""
                        loading="lazy"
                        className="w-24 h-24 shrink-0 object-contain rounded-lg border border-border bg-white p-1"
                      />
                    ) : (
                      <div className="w-24 h-24 shrink-0 rounded-lg border border-border bg-bg" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-mono text-text-muted">{p.code}</div>
                      <div className="text-base font-semibold text-text">{p.name}</div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1">
                    {p.sizes.map((s) => (
                      <div
                        key={s.label}
                        className="flex items-center justify-between gap-3 rounded-md bg-bg px-3 py-2 text-sm"
                      >
                        <span className="text-text">{s.label}</span>
                        {s.qty === null ? (
                          <span className="text-text-muted" title="Variantu se nepodařilo dohledat">
                            stav neznámý
                          </span>
                        ) : s.qty > 0 ? (
                          <span className="font-semibold text-action-dark">
                            Skladem: {s.qty} ks
                          </span>
                        ) : (
                          <span className="font-semibold text-danger">Není skladem</span>
                        )}
                      </div>
                    ))}
                    {p.sizes.length === 0 && (
                      <p className="text-xs text-text-muted">Kategorie nemá vyplněné velikosti.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </>
  );
}
